/**
 * Kohler Frontend Logger
 * - Console + Remote (Azure App Insights) logging
 * - Configurable levels, sanitization, and transports
 */

import {
  shouldLog,
  sanitize,
  consoleWrite,
  buildBaseLogData,
} from "./loggerUtils.js"

let getAppInsights
let getCurrentPageViewId
if (typeof window !== "undefined") {
  import("../appInsights.js")
    .then(module => {
      getAppInsights = module.getAppInsights
      getCurrentPageViewId = module.getCurrentPageViewId
    })
    .catch(error => {
      try {
        console &&
          console.warn &&
          console.warn("[KohlerLogger] Failed to load App Insights:", error)
      } catch (_) {
        // swallow
      }
    })
}

const defaultConfig = {
  /** Controls minimum log level to process - filters out noise in production */
  level: (process.env.NEXT_PUBLIC_LOG_LEVEL || "INFO").toUpperCase(),

  /** Controls browser console output - disable in production for performance */
  consoleEnabled: process.env.NEXT_PUBLIC_LOG_CONSOLE_ENABLED !== "false",

  /** Controls Azure App Insights telemetry - disable to prevent data collection */
  remoteEnabled:
    process.env.NEXT_PUBLIC_LOG_REMOTE_ENABLED === "false" ? false : true,

  /** Prevents infinite recursion when logging complex nested objects */
  maxDepth: 4,

  /** Prevents memory issues when logging large arrays - truncates for performance */
  maxArrayLength: 50,

  /** Prevents log bloat from huge strings - truncates to keep logs readable */
  maxStringLength: 1000,

  /** Adds consistent prefix to all log contexts for multi-app identification */
  globalContext: "",

  /** Enables user tracking in logs for debugging user-specific issues */
  getUserId: null,

  /** Allows extending logger with custom destinations (Slack, Sentry, etc.) */
  transports: [],
}

let runtimeConfig = { ...defaultConfig }

/**
 * Updates the logger configuration with partial config object
 * @param {object} partial - Partial configuration object to merge with current config
 * @return {void}
 */
function setConfig(partial) {
  runtimeConfig = { ...runtimeConfig, ...partial }
}

/**
 * Builds structured log data with context, timestamp, and page information
 * @param {string} instanceContext - Logger instance context
 * @param {string} level - Log level
 * @param {string} message - Log message
 * @param {object} meta - Additional metadata
 * @return {object} Structured log data object
 */
function buildLogData(instanceContext, level, message, meta) {
  const page =
    typeof window !== "undefined" && window.location?.pathname
      ? window.location.pathname
      : "server"
  let pageViewId
  try {
    pageViewId = getCurrentPageViewId ? getCurrentPageViewId() : undefined
  } catch (_) {
    pageViewId = undefined
  }

  const baseData = buildBaseLogData(
    instanceContext,
    level,
    message,
    runtimeConfig,
    page
  )
  const sanitizedMeta = sanitize(meta, 0, new WeakSet(), runtimeConfig)
  const reserved = new Set([
    "level",
    "message",
    "context",
    "timestamp",
    "page",
    "userId",
    "pageViewId",
  ])
  const filteredMeta = {}
  if (sanitizedMeta && typeof sanitizedMeta === "object") {
    for (const [k, v] of Object.entries(sanitizedMeta)) {
      if (!reserved.has(k)) filteredMeta[k] = v
    }
  }
  return { ...filteredMeta, ...baseData, pageViewId }
}

/**
 * Sends log data to remote logging services (Azure App Insights) and custom transports
 * @param {string} level - Log level
 * @param {string} instanceContext - Logger instance context
 * @param {string} message - Log message
 * @param {object} logData - Structured log data
 * @param {object} rawMeta - Raw metadata (may contain Error objects)
 * @return {void}
 */
function remoteWrite(level, instanceContext, message, logData, rawMeta) {
  if (!runtimeConfig.remoteEnabled) return

  try {
    const appInsights = getAppInsights ? getAppInsights() : null
    const formattedMessage = `${
      instanceContext ? `[${instanceContext}]` : ""
    } ${message}`

    if (appInsights) {
      if (level === "ERROR" && rawMeta && rawMeta.error instanceof Error) {
        appInsights.trackException({
          exception: rawMeta.error,
          properties: { ...logData, error: undefined },
          severityLevel: 3,
        })
      } else {
        const severityMap = { ERROR: 3, WARN: 2, INFO: 1, DEBUG: 0 }
        appInsights.trackTrace({
          message: formattedMessage,
          severityLevel: severityMap[level] || 1,
          properties: logData,
        })
      }
      if (level === "ERROR" && process.env.NODE_ENV === "development") {
        appInsights.flush()
      }
    }

    // Call custom transports regardless of App Insights status
    if (Array.isArray(runtimeConfig.transports)) {
      runtimeConfig.transports.forEach(fn => {
        try {
          typeof fn === "function" && fn(level, message, logData)
        } catch (e) {
          // Transport failures should not break app
          try {
            console &&
              console.warn &&
              console.warn("[KohlerLogger] transport failed", e)
          } catch (_) {
            // swallow
          }
        }
      })
    }
  } catch (e) {
    try {
      console &&
        console.warn &&
        console.warn("[KohlerLogger] remote logging failed", e)
    } catch (_) {
      // swallow
    }
  }
}

/**
 * Internal logger implementation for global logger
 * @param {string} context - Context string for this logger instance
 * @return {object} Logger instance with methods
 */
function createLogger(context = "") {
  const timers = new Map()

  /**
   * Internal logging method that handles both console and remote logging
   * @param {string} level - Log level
   * @param {string} message - Log message
   * @param {object} meta - Additional metadata
   * @return {void}
   */
  function _log(level, message, meta = {}) {
    try {
      if (!shouldLog(level, runtimeConfig.level)) return

      const contextStr = [runtimeConfig.globalContext, context]
        .filter(Boolean)
        .join(":")

      let logData
      try {
        logData = buildLogData(contextStr, level, message, meta)
      } catch (_) {
        // Ensure logging never throws due to data shaping
        logData = {
          level,
          message,
          context: contextStr,
          timestamp: new Date().toISOString(),
        }
      }

      try {
        consoleWrite(
          level,
          contextStr,
          message,
          meta,
          runtimeConfig.consoleEnabled
        )
      } catch (_) {
        // no-op
      }

      try {
        remoteWrite(level, context, message, logData, meta)
      } catch (_) {
        // no-op
      }
    } catch (_) {
      // Absolute last-resort guard: never let logging break the app
      try {
        console && console.log && console.log(`[${level}] ${message}`)
      } catch (__) {
        // no-op
      }
    }
  }

  return {
    // Core logging methods
    error: (message, meta = {}) => _log("ERROR", message, meta),
    warn: (message, meta = {}) => _log("WARN", message, meta),
    info: (message, meta = {}) => _log("INFO", message, meta),
    debug: (message, meta = {}) => _log("DEBUG", message, meta),

    // Child logger creation
    child: childContext => {
      const fullContext = [context, childContext].filter(Boolean).join(":")
      return createLogger(fullContext)
    },

    // Timer methods
    timerStart: label => timers.set(label, Date.now()),
    timerEnd: (label, meta = {}, level = "INFO") => {
      const start = timers.get(label)
      if (start) {
        const durationMs = Date.now() - start
        timers.delete(label)
        _log(level, `${label} duration`, { ...meta, durationMs })
      }
    },

    // Context getter
    getContext: () =>
      [runtimeConfig.globalContext, context].filter(Boolean).join(":"),
  }
}

// Main logger instance
const logger = createLogger()

/**
 * Creates a new logger instance with the specified context
 * @param {string} contextName - Context name for the logger
 * @return {object} New logger instance
 */
export const createContextLogger = contextName => createLogger(contextName)

/**
 * Initializes the global logger on window.kohler.logger with various methods
 * @return {void}
 */
export const initializeGlobalLogger = () => {
  if (typeof window !== "undefined") {
    try {
      if (!window.kohler) window.kohler = {}

      window.kohler.logger = {
        // Core logging methods
        error: (message, metadata = {}) => logger.error(message, metadata),
        warn: (message, metadata = {}) => logger.warn(message, metadata),
        info: (message, metadata = {}) => logger.info(message, metadata),
        debug: (message, metadata = {}) => logger.debug(message, metadata),

        // Context-based logger creation
        createContextLogger: contextName => createLogger(contextName),
        getLogger: contextName => createLogger(contextName),

        // Auto page context logger
        getPageContextLogger: componentName => {
          const currentPage =
            window.location?.pathname?.split("/").pop() || "home"
          const fullContext = componentName
            ? `${currentPage}:${componentName}`
            : currentPage
          return createLogger(fullContext)
        },

        // Configuration controls
        setLevel: lvl => setConfig({ level: (lvl || "INFO").toUpperCase() }),
        enableConsole: () => setConfig({ consoleEnabled: true }),
        disableConsole: () => setConfig({ consoleEnabled: false }),
        enableRemote: () => setConfig({ remoteEnabled: true }),
        disableRemote: () => setConfig({ remoteEnabled: false }),
        setGlobalContext: ctx => setConfig({ globalContext: ctx || "" }),
        setUserResolver: fn =>
          setConfig({ getUserId: typeof fn === "function" ? fn : null }),
        addTransport: fn =>
          setConfig({
            transports: [...runtimeConfig.transports, fn].filter(Boolean),
          }),
        getConfig: () => ({ ...runtimeConfig }),
      }

      try {
        console &&
          console.info &&
          console.info(
            "[KohlerLogger] Global logger initialized at window.kohler.logger"
          )
      } catch (_) {
        // swallow
      }
    } catch (_) {
      // swallow - global logger init must never break
    }
  }
}

if (typeof window !== "undefined") {
  initializeGlobalLogger()
}

// Clean, short export functions for import usage (alternative to global logger)
/**
 * Shorthand for error logging using the main logger
 * @param {string} message - Error message to log
 * @param {object} metadata - Additional metadata
 * @return {void}
 */
export const error = (message, metadata) => logger.error(message, metadata)

/**
 * Shorthand for warning logging using the main logger
 * @param {string} message - Warning message to log
 * @param {object} metadata - Additional metadata
 * @return {void}
 */
export const warn = (message, metadata) => logger.warn(message, metadata)

/**
 * Shorthand for info logging using the main logger
 * @param {string} message - Info message to log
 * @param {object} metadata - Additional metadata
 * @return {void}
 */
export const info = (message, metadata) => logger.info(message, metadata)

/**
 * Shorthand for debug logging using the main logger
 * @param {string} message - Debug message to log
 * @param {object} metadata - Additional metadata
 * @return {void}
 */
export const debug = (message, metadata) => logger.debug(message, metadata)

/**
 * Shorthand for setting log level using the main logger
 * @param {string} level - Log level to set
 * @return {void}
 */
export const setLevel = level =>
  setConfig({ level: (level || "INFO").toUpperCase() })

// Default export
export default logger
